import React from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
} from "reactstrap";
import { getMaxBirthDate } from "../../InputsValidations.js";

const ExternalOwnerInformation = ({
  formData,
  handleInputChange,
  editingOwnerIndex,
  handleAddNewOwner,
  handleUpdateOwner,
  handleCancelOwnerEdit,
  handleEditOwner,
  confirmDeleteOwner,
  setShowContactModal,
  setContactToEdit,
  handleEditContact,
  handleDeleteContact,
  setShowAddressModal,
  setAddressToEdit,
  handleEditAddress,
  handleDeleteAddress,
  isOwnerComplete,
}) => {
  // Calculate total ownership percentage from saved owners (excluding current owner being edited)
  const calculateTotalOwnership = () => {
    let total = 0;
    formData.owners.forEach((owner, index) => {
      // Skip the owner being edited
      if (index !== editingOwnerIndex) {
        total += parseFloat(owner.ownershipPercentage) || 0;
      }
    });
    // Add current owner's ownership
    total += parseFloat(formData.currentOwner.ownershipPercentage) || 0;
    return total;
  };

  const totalOwnership = calculateTotalOwnership();
  const ownershipExceeds100 = totalOwnership > 100;

  return (
    <div>
      {/* Show list of added owners */}
      {formData.owners.length > 0 && (
        <Card className="mb-3">
          <CardHeader>
            <h5 className="mb-0">
              <i className="ni ni-circle-08 mr-2" />
              Added Owners ({formData.owners.length})
            </h5>
          </CardHeader>
          <CardBody>
            {formData.owners.map((owner, index) => (
              <div key={index} className="border rounded p-2 mb-2">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h6 className="mb-1">
                      {owner.firstName} {owner.middleName} {owner.lastName}
                    </h6>
                    <p className="mb-1 text-sm">
                      <strong>Ownership:</strong> {owner.ownershipPercentage}%
                    </p>
                    <p className="mb-0 text-sm">
                      <strong>Communication Methods:</strong>{" "}
                      {owner.contacts.length} | <strong>Addresses:</strong>{" "}
                      {owner.addresses.length}
                    </p>
                  </div>
                   <div className="btn-group">
                     <Button
                       color="primary"
                       size="md"
                       title="Edit owner"
                       onClick={() => handleEditOwner(index)}
                       disabled={editingOwnerIndex === index}
                     >
                       <i className="ni ni-settings-gear-65" />
                     </Button>
                     <Button
                       color="secondary"
                       size="md"
                       title="Remove owner"
                       onClick={() => confirmDeleteOwner(index)}
                     >
                       <i className="ni ni-fat-remove" />
                     </Button>
                   </div>
                </div>
              </div>
            ))}
          </CardBody>
        </Card>
      )}

      <Card className="mb-3">
        <CardHeader>
          <h5 className="mb-0">
            <i className="ni ni-single-02 mr-2" />
            {editingOwnerIndex !== null
              ? `Editing Owner #${editingOwnerIndex + 1}`
              : formData.owners.length > 0
              ? "Add Another Owner"
              : "Owner Information"}
          </h5>
        </CardHeader>
        <CardBody>
          {/* Ownership warning */}
          {ownershipExceeds100 && (
            <Alert color="warning" className="mb-3">
              <i className="ni ni-notification-70 mr-2" />
              Total ownership percentage cannot exceed 100%. Current total:{" "}
              {totalOwnership.toFixed(2)}%
            </Alert>
          )}

          <Row>
            <Col md="3">
              <FormGroup className="mb-3">
                <Label htmlFor="ownerFirstName" className="form-control-label">
                  First Name *
                </Label>
                <Input
                  id="ownerFirstName"
                  type="text"
                  value={formData.currentOwner.firstName}
                  onChange={(e) =>
                    handleInputChange("firstName", e.target.value, false, true)
                  }
                  maxLength={255}
                  bsSize="sm"
                />
              </FormGroup>
            </Col>
            <Col md="3">
              <FormGroup className="mb-3">
                <Label htmlFor="ownerMiddleName" className="form-control-label">
                  Middle Name
                </Label>
                <Input
                  id="ownerMiddleName"
                  type="text"
                  value={formData.currentOwner.middleName}
                  onChange={(e) =>
                    handleInputChange("middleName", e.target.value, false, true)
                  }
                  maxLength={255}
                  bsSize="sm"
                />
              </FormGroup>
            </Col>
            <Col md="3">
              <FormGroup className="mb-3">
                <Label htmlFor="ownerLastName" className="form-control-label">
                  Last Name *
                </Label>
                <Input
                  id="ownerLastName"
                  type="text"
                  value={formData.currentOwner.lastName}
                  onChange={(e) =>
                    handleInputChange("lastName", e.target.value, false, true)
                  }
                  maxLength={255}
                  bsSize="sm"
                />
              </FormGroup>
            </Col>
            <Col md="3">
              <FormGroup className="mb-3">
                <Label htmlFor="ownerGender" className="form-control-label">
                  Gender *
                </Label>
                <Input
                  id="ownerGender"
                  type="select"
                  value={formData.currentOwner.gender}
                  onChange={(e) =>
                    handleInputChange("gender", e.target.value, false, true)
                  }
                  bsSize="sm"
                >
                  <option value="MALE">Male</option>
                  <option value="FEMALE">Female</option>
                  <option value="OTHER">Other</option>
                </Input>
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="3">
              <FormGroup className="mb-3">
                <Label htmlFor="ownerBirthDate" className="form-control-label">
                  Birth Date *
                </Label>
                <Input
                  id="ownerBirthDate"
                  type="date"
                  value={formData.currentOwner.birthDate}
                  onChange={(e) =>
                    handleInputChange("birthDate", e.target.value, false, true)
                  }
                  max={getMaxBirthDate()}
                  bsSize="sm"
                />
              </FormGroup>
            </Col>
            <Col md="3">
              <FormGroup className="mb-3">
                <Label
                  htmlFor="ownerCitizenship"
                  className="form-control-label"
                >
                  Citizenship *
                </Label>
                <Input
                  id="ownerCitizenship"
                  type="text"
                  value={formData.currentOwner.citizenship}
                  onChange={(e) =>
                    handleInputChange(
                      "citizenship",
                      e.target.value,
                      false,
                      true
                    )
                  }
                  placeholder="US"
                  maxLength={10}
                  bsSize="sm"
                />
              </FormGroup>
            </Col>
            <Col md="3">
              <FormGroup className="mb-3">
                <Label
                  htmlFor="ownerOwnershipPercentage"
                  className="form-control-label"
                >
                  Ownership % *
                </Label>
                <Input
                  id="ownerOwnershipPercentage"
                  type="text"
                  inputMode="decimal"
                  placeholder="0.0"
                  value={formData.currentOwner.ownershipPercentage}
                  onChange={(e) =>
                    handleInputChange(
                      "ownershipPercentage",
                      e.target.value,
                      false,
                      true
                    )
                  }
                  maxLength={5}
                  bsSize="sm"
                />
              </FormGroup>
            </Col>
            <Col md="3">
              <FormGroup className="mb-3">
                <Label htmlFor="ownerSSN" className="form-control-label">
                  SSN
                </Label>
                <Input
                  id="ownerSSN"
                  type="text"
                  value={formData.currentOwner.ssn}
                  onChange={(e) =>
                    handleInputChange("ssn", e.target.value, false, true)
                  }
                  placeholder="XXX-XX-XXXX"
                  maxLength={11}
                  bsSize="sm"
                />
              </FormGroup>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Owner Communication Methods Section */}
      <Card className="mb-3">
        <CardHeader>
          <h5 className="mb-0">
            <i className="ni ni-mobile-button mr-2" />
            Communication Methods *
          </h5>
           <p className="text-sm text-muted mb-0">
             At least one primary email and one primary phone required
           </p>
        </CardHeader>
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Button
              color="primary"
              size="sm"
              onClick={() => {
                setContactToEdit(null);
                setShowContactModal(true);
              }}
            >
              <i className="ni ni-fat-add mr-2" />
              Add Communication Method
            </Button>
             <div className="text-sm">
               <span
                 className={
                   formData.currentOwner.contacts.some(
                     (c) => c.type === "Email" && c.primary === "Yes"
                   )
                     ? "text-success"
                     : "text-muted"
                 }
               >
                 <i
                   className={`ni ${
                     formData.currentOwner.contacts.some(
                       (c) => c.type === "Email" && c.primary === "Yes"
                     )
                       ? "ni-check-bold"
                       : "ni-fat-remove"
                   } mr-1`}
                 />
                 Email Primary
               </span>
               {" | "}
               <span
                 className={
                   formData.currentOwner.contacts.some(
                     (c) => c.type === "Phone" && c.primary === "Yes"
                   )
                     ? "text-success"
                     : "text-muted"
                 }
               >
                 <i
                   className={`ni ${
                     formData.currentOwner.contacts.some(
                       (c) => c.type === "Phone" && c.primary === "Yes"
                     )
                       ? "ni-check-bold"
                       : "ni-fat-remove"
                   } mr-1`}
                 />
                 Phone Primary
               </span>
             </div>
          </div>

          {formData.currentOwner.contacts.length === 0 ? (
            <p className="text-muted mb-0">
              No communication methods added yet.
            </p>
          ) : (
            formData.currentOwner.contacts.map((contact, index) => (
              <div key={contact.id} className="border rounded p-2 mb-2">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="flex-grow-1">
                    <span className="font-weight-bold mr-2">
                      {contact.type}:
                    </span>
                    <span className="mr-2">{contact.value}</span>
                    {contact.primary === "Yes" && (
                      <span className="badge badge-sm badge-secondary">
                        Primary
                      </span>
                    )}
                  </div>
                   <div className="btn-group">
                     <Button
                       color="primary"
                       size="md"
                       title="Edit communication method"
                       onClick={() => handleEditContact(contact)}
                     >
                       <i className="ni ni-settings-gear-65" />
                     </Button>
                     <Button
                       color="secondary"
                       size="md"
                       title="Remove communication method"
                       onClick={() => handleDeleteContact(contact.id)}
                     >
                       <i className="ni ni-fat-remove" />
                     </Button>
                   </div>
                </div>
              </div>
            ))
          )}
        </CardBody>
      </Card>

      {/* Owner Addresses Section */}
      <Card className="mb-3">
        <CardHeader>
          <h5 className="mb-0">
            <i className="ni ni-pin-3 mr-2" />
            Owner Addresses *
          </h5>
          <p className="text-sm text-muted mb-0">At least one address required</p>
        </CardHeader>
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Button
              color="primary"
              size="sm"
              onClick={() => {
                setAddressToEdit(null);
                setShowAddressModal(true);
              }}
            >
              <i className="ni ni-fat-add mr-2" />
              Add Address
            </Button>
            <small className="text-muted">
              You can add multiple addresses of any type
            </small>
          </div>

          {formData.currentOwner.addresses.length === 0 ? (
            <p className="text-muted mb-0">No addresses added yet.</p>
          ) : (
            formData.currentOwner.addresses.map((address, index) => (
              <div key={address.id} className="border rounded p-2 mb-2">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h6 className="mb-1">{address.addressType}</h6>
                    <p className="mb-0 text-muted text-sm">
                      {[
                        address.streetAddress,
                        address.city,
                        address.state,
                        address.zipCode,
                      ]
                        .filter((field) => field && field.trim() !== "")
                        .join(", ")}
                    </p>
                  </div>
                   <div className="btn-group">
                     <Button
                       color="primary"
                       size="md"
                       title="Edit address"
                       onClick={() => handleEditAddress(address)}
                     >
                       <i className="ni ni-settings-gear-65" />
                     </Button>
                     <Button
                       color="secondary"
                       size="md"
                       title="Remove address"
                       onClick={() => handleDeleteAddress(address.id)}
                     >
                       <i className="ni ni-fat-remove" />
                     </Button>
                   </div>
                </div>
              </div>
            ))
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default ExternalOwnerInformation;

